import { useMsal } from '@azure/msal-react';
import { scopes } from '../../../../authConfig';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { useEffect, useState } from 'react';
import { Achievement, ActionDto } from '../../../../dto/reporting-models';
import { App, Card } from 'antd';
import { AchievementCard } from './achievement-card';
import { AchievementForm } from './achievement-form';
import { reportingService } from '../../../../services/reporting-service';
import { strings } from '../../../../lang';

export const AdminAchievements = () => {
  const { instance, inProgress } = useMsal();
  const { message } = App.useApp();
  const [displayAchievementForm, shouldDisplayAchievementForm] = useState<boolean>(false);
  const [displayAddButton, shouldDisplayAddButton] = useState<boolean>(true);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [actionsInfo, setActions] = useState<ActionDto[]>([]);

  useEffect(() => {
    const fetchActionsAsync = async () => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      try {
        const actionsResponse = await reportingService.getActionsAsync(token.accessToken);
        setActions(actionsResponse);
      } catch (error) {}
    };

    fetchActionsAsync();
    fetchAchievementsAsync();
  }, []);

  const fetchAchievementsAsync = async () => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    try {
      const achievementsResponse = await reportingService.getAchievementsAsync(token.accessToken);
      setAchievements(achievementsResponse);
    } catch (error) {}
  };

  const updateAchievementsAsync = async (achs: Achievement[]) => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    try {
      await reportingService.postAchievementsAsync(token.accessToken, achs);
    } catch (error) {}
  };

  const addAchievement = () => {
    const newAchievement = {
      id: 0,
      name: '',
      description: '',
      points: 0,
      target: 0,
      actions: [],
      days: undefined,
      fromHour: undefined,
      toHour: undefined,
      icon: undefined,
      dueDate: undefined,
      isAccumulative: false,
    } as Achievement;
    setAchievements([...achievements, newAchievement]);
    shouldDisplayAchievementForm(true);
    shouldDisplayAddButton(false);
  };

  const handleAddCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      addAchievement();
    }
  };

  const onCancel = () => {
    const newAchievements = achievements.filter((r) => r.id !== 0);
    shouldDisplayAchievementForm(false);
    shouldDisplayAddButton(true);
    setAchievements(newAchievements);
  };

  const onSubmit = async (achievement: Achievement) => {
    const newAchievements = achievements.map((r) => {
      if (r.id === achievement.id) {
        return achievement;
      }
      return r;
    });

    await updateAchievementsAsync(newAchievements);
    await fetchAchievementsAsync();

    shouldDisplayAchievementForm(false);
    shouldDisplayAddButton(true);
  };

  const deleteAchievementAsync = async (id: number) => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    try {
      const response = await reportingService.deleteAchievementAsync(token.accessToken, id);
      if (response.status === 200) {
        const newAchievements = achievements.filter((r) => r.id !== id);
        setAchievements(newAchievements);
      } else {
        message.error(strings.gamificationSettings.achievements.deleteFailed);
      }
    } catch (error) {}
  };

  return (
    <div className="container flex-admin-vertical-container" style={{ width: 900 }}>
      {displayAddButton && (
        <Card className="achievement-card achievement-card--add" role="button" aria-label={strings.gamificationSettings.achievements.add} tabIndex={0} onClick={addAchievement} onKeyDown={handleAddCardKeyDown}>
          <div className="achievement-card__add-content">
            <span className="achievement-card__add-icon" aria-hidden="true" />
            <div>
              <strong>{strings.gamificationSettings.achievements.add}</strong>
              <span>{strings.gamificationSettings.achievements.addDescription}</span>
            </div>
          </div>
        </Card>
      )}
      {displayAchievementForm && <AchievementForm achievement={achievements.find((x) => x.id === 0)!} actions={actionsInfo} onSubmit={onSubmit} cancel={onCancel} />}
      {achievements
        .filter((ach) => ach.id !== 0)
        .map((ach) => (
          <AchievementCard key={ach.id} achievement={ach} actionsInfo={actionsInfo} shouldDisplayAddButton={shouldDisplayAddButton} onSubmit={onSubmit} deleteAchievement={deleteAchievementAsync} />
        ))}
    </div>
  );
};
